// Design tokens for the reconciliation module. Ported verbatim from
// /tmp/erbi-design/erbi/project/primitives.jsx (lines 3-27).
//
// Scoped to this module — does NOT refactor any existing ERBI tokens.

export const TOKENS = {
  bg:           'oklch(0.985 0.003 250)',
  panel:        'oklch(1 0 0)',
  panelAlt:     'oklch(0.975 0.004 250)',
  border:       'oklch(0.92 0.006 250)',
  borderStrong: 'oklch(0.86 0.008 250)',
  ink:          'oklch(0.18 0.012 260)',
  ink2:         'oklch(0.42 0.012 260)',
  ink3:         'oklch(0.62 0.010 255)',
  accent:       'oklch(0.55 0.15 260)',
  accentSoft:   'oklch(0.94 0.03 260)',
  select:       'oklch(0.55 0.15 260)',
  selectSoft:   'oklch(0.96 0.035 260)',
  danger:       'oklch(0.58 0.18 25)',
  warn:         'oklch(0.70 0.14 70)',
  ok:           'oklch(0.62 0.13 150)',
  headerBg:     'oklch(0.18 0.014 260)',
  headerInk:    'oklch(0.98 0.004 250)',
};

// Both constants point at the shared Inter stack (src/typography.css).
// MONO_FONT is kept (repointed, not deleted) because the name is
// load-bearing across ~100 inline-style call sites.
export const UI_FONT = 'var(--font-ui)';
export const MONO_FONT = 'var(--font-ui)';

// Spreadable numeric styles. Inline styles win over CSS classes, so the
// .num/.code utility classes can't reach inline-styled cells — spread these
// instead. Amounts get tabular digits only; identifiers (SAP doc numbers,
// references, invoice numbers) additionally get the slashed zero.
export const NUM_STYLE = {
  fontFamily: 'var(--font-ui)',
  fontVariantNumeric: 'tabular-nums',
};
export const CODE_STYLE = {
  ...NUM_STYLE,
  fontFeatureSettings: '"zero" 1',
};

export function laneColor(hue, l = 0.62, c = 0.14) {
  return `oklch(${l} ${c} ${hue})`;
}
export function laneSoft(hue) {
  return `oklch(0.96 0.03 ${hue})`;
}

// Match category → SAP-side display hue. From recon-data.jsx:7-13.
// vendor_invoice=222, intercompany=260, clearing=35, accrual=290, other=150.
export const SAP_CATEGORIES = [
  { id: 'vendor_invoice', name: 'Vendor invoice',     hue: 222 },
  { id: 'intercompany',   name: 'Intercompany',       hue: 260 },
  { id: 'clearing',       name: 'Clearing',           hue: 35  },
  { id: 'accrual',        name: 'Accrual reversal',   hue: 290 },
  { id: 'other',          name: 'Other',              hue: 150 },
];

// Plain-English explanations for each reconciliation category, written to be
// readable aloud to non-technical stakeholders. Keyed by the rolled-up group
// id used in the Reconciled tab and the Budget modal. Defined once here so the
// two surfaces never drift.
export const CATEGORY_HINTS = {
  vendor_invoice:
    'Real supplier invoices we sent to AP that SAP now shows as posted. ' +
    'These are the heart of the reconciliation — each is matched to one of our ' +
    'invoices by reference, by amount, or manually.',
  unmatched:
    'SAP posted this spend but no invoice is linked here yet — the invoice may ' +
    'not be uploaded, or none is expected (subscriptions, fees). It still counts ' +
    'toward actual spend; matching to an invoice is just for audit.',
  intercompany:
    'SAP doc type ZC — postings between group companies, not external supplier ' +
    'invoices. There is nothing we sent to AP to match them against, so they are ' +
    'parked here for visibility. A negative amount is just a credit-side posting.',
  clearing:
    'SAP doc type AB — signed offset entries that usually cancel out an ' +
    'intercompany ZC posting. A NEGATIVE amount is the credit/offset leg: a ' +
    'paired AB (−) and ZC (+) net to zero, so they do not move the budget. ' +
    'An AB with no partner is a “clearing orphan” — worth a look, since it was ' +
    'expected to net out but is sitting alone.',
  accrual:
    'Reversals of accruals booked in a prior period (Reference begins “VSSC”). ' +
    'A negative amount unwinds an accrual recorded earlier, so it reduces spend. ' +
    'An unmatched one is an “accrual orphan”.',
  other:
    'SAP rows whose document type we do not recognise (not KR/LG/ZC/AB and not a ' +
    'VSSC accrual). Flagged for client review.',
};

// Format a money value with the right currency sign + minus glyph. From
// recon-data.jsx:185-189.
export function fmtMoney(n, currency = 'EUR') {
  if (n == null || isNaN(n)) return '';
  const sym = { EUR: '€', USD: '$', GBP: '£' }[currency] || '';
  const abs = Math.abs(n);
  return (
    (n < 0 ? '−' : '') +
    sym +
    abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
}

// Confidence tier — used by the confidence chip in the queue rows.
export function confTier(c) {
  if (c >= 0.7) return 'good';
  if (c >= 0.4) return 'soft';
  return 'bad';
}
